import FeatherIcon from 'feather-icons-react';
import { Fragment, useContext, useMemo, useState } from 'react';
import {
  IconContainer,
  TeamsDropdownCTA,
  TeamsMenuContainer,
  TeamsMenuItem,
  TeamsSectionTitle,
  TeamsDropdownFooter,
  TeamsStatusMessage,
  MenuDivider,
  CustomDropdown,
} from './styles';
import Tooltip from 'components/tooltip';
import Button from 'components/button/Button';
import { IconTeam } from './IconTeam';
import { Team } from 'contexts/teams/types';
import { SessionContext } from 'contexts/session';
import Checkbox from 'components/checkbox';
import Icon from 'components/icons';
import {
  accountOwnerDefaultOption,
  noTeamOption,
  removeOption,
  teamManagerDefaultOption,
} from 'constants/components/teamSelect';
import { TeamsMenuProps, TeamsMenuUnassignProps, TeamsSelectorProps } from './types';
import { useTeamsMenuSelection } from './useTeamsMenuSelection';

const getActiveUsersCountLabel = (team: Team) => {
  const activeUsers = team.active_users_count || 0;
  return `${activeUsers} active ${activeUsers === 1 ? 'user' : 'users'}`;
};

export const TeamsMenu = ({
  onClose,
  onTeamSelect,
  withSubmit,
  withUsersCount,
  accountTeams,
  teamsSelected,
  account,
  full,
  selectAllOption,
  teamRemoved,
  analyticsTheme,
}: TeamsMenuProps): JSX.Element => {
  const { isAccountOwner } = useContext(SessionContext);
  const { selected, handleTeamSelection, handleTeamChangeSubmit, handleClose } =
    useTeamsMenuSelection({
      teamsSelected,
      withSubmit,
      onTeamSelect,
      onClose,
      teamRemoved,
    });

  const selectAllLabel = analyticsTheme
    ? selectAllOption
      ? selectAllOption
      : isAccountOwner
      ? accountOwnerDefaultOption
      : teamManagerDefaultOption
    : removeOption;

  return (
    <TeamsMenuContainer withSubmit={withSubmit} full={full}>
      <div className="teams-options-container">
        <>
          <TeamsMenuItem
            key={noTeamOption}
            type={'button'}
            onClick={() => handleTeamSelection(noTeamOption)}
          >
            <Checkbox style={{ marginRight: '10px' }} checked={selected.length === 0} controlled />
            {analyticsTheme ? (
              <IconTeam size={'smaller'} fallbackIcon={account?.logo_url} />
            ) : (
              <IconContainer size={'smaller'}>
                <span className="remove-team">
                  <Icon icon={'Block'} />
                </span>
              </IconContainer>
            )}
            <div className="team-container">
              <span className={'teams-label'}>{selectAllLabel}</span>
            </div>
          </TeamsMenuItem>
          <MenuDivider />
        </>
        {accountTeams?.map((item) => (
          <Fragment key={item.id}>
            <TeamsMenuItem
              withActiveUsers={withUsersCount}
              key={item.id}
              type={'button'}
              onClick={() => handleTeamSelection(item.id)}
            >
              <Checkbox
                style={{ marginRight: '10px' }}
                checked={selected.length === 0 ? item.id === '' : selected.includes(item.id)}
                controlled
              />
              <IconTeam
                iconName={item.icon?.name}
                color={item.icon?.color}
                size={'smaller'}
                fallbackIcon={account?.logo_url}
              />
              <div className="team-container">
                <div className={'teams-label'}>{item.name}</div>
                {withUsersCount && (
                  <div className="active-users-label">{getActiveUsersCountLabel(item)}</div>
                )}
              </div>
            </TeamsMenuItem>
            <MenuDivider />
          </Fragment>
        ))}
      </div>
      {!withSubmit && (
        <TeamsDropdownCTA>
          <Button $secondary onClick={handleClose}>
            Cancel
          </Button>
          <Button $primary disabled={!selected} onClick={handleTeamChangeSubmit}>
            Update team
          </Button>
        </TeamsDropdownCTA>
      )}
    </TeamsMenuContainer>
  );
};

export const TeamsMenuUnassignPlaylist = ({
  onClose,
  onSubmit,
  withSubmit,
  withUsersCount,
  accountTeams,
  teamsSelected,
  account,
  full,
  teamRemoved,
}: TeamsMenuUnassignProps): JSX.Element => {
  const getUnassignedTeamIds = (selectedIds: string[]) => {
    if (!teamsSelected) return [];
    return teamsSelected
      .map((team) => team.team_id)
      .filter((teamId): teamId is string => Boolean(teamId) && !selectedIds.includes(teamId));
  };

  const handleUnassignSelect = (selectedIds: string[]) => {
    onSubmit(getUnassignedTeamIds(selectedIds));
  };

  const { selected, handleTeamSelection, handleTeamChangeSubmit } = useTeamsMenuSelection({
    teamsSelected,
    withSubmit: false,
    onTeamSelect: handleUnassignSelect,
    onClose,
    teamRemoved,
  });

  const unassignedTeamIds = useMemo(
    () => getUnassignedTeamIds(selected),
    [selected, teamsSelected]
  );
  const hasUnassignedItems = useMemo(() => unassignedTeamIds.length > 0, [unassignedTeamIds]);

  return (
    <TeamsMenuContainer withSubmit={true} full={full} wide={true} itemsCount={accountTeams.length}>
      <div className="teams-options-container teams-options-container--with-submit">
        <TeamsSectionTitle>Deselect teams to unassign this playlist</TeamsSectionTitle>
        <MenuDivider />
        {accountTeams?.map((item) => (
          <Fragment key={item.id}>
            <TeamsMenuItem
              withActiveUsers={withUsersCount}
              key={item.id}
              type={'button'}
              onClick={() => handleTeamSelection(item.id)}
            >
              <Checkbox
                style={{ marginRight: '10px' }}
                checked={selected.length === 0 ? item.id === '' : selected.includes(item.id)}
                controlled
              />
              <IconTeam
                iconName={item.icon?.name}
                color={item.icon?.color}
                size={'smaller'}
                fallbackIcon={account?.logo_url}
              />
              <div className="team-container">
                <div className={'teams-label'} style={{ fontWeight: 700, textAlign: 'left' }}>
                  {item.name}
                </div>
                {withUsersCount && (
                  <div className="active-users-label">{getActiveUsersCountLabel(item)}</div>
                )}
              </div>
            </TeamsMenuItem>
            <MenuDivider />
          </Fragment>
        ))}
      </div>
      <TeamsDropdownFooter>
        {hasUnassignedItems && (
          <TeamsStatusMessage>
            <span>{unassignedTeamIds.length} team(s)</span> selected to unassign
          </TeamsStatusMessage>
        )}
        <Button
          $primary
          disabled={!hasUnassignedItems}
          size="small"
          onClick={handleTeamChangeSubmit}
        >
          Save changes
        </Button>
      </TeamsDropdownFooter>
    </TeamsMenuContainer>
  );
};

const TeamsSelector = ({
  onTeamChange,
  withSubmit,
  accountTeams,
  withEditBtn = true,
  emptyLabel = 'Unassigned team',
  editBtnRight,
  maxWidth,
  userTeams,
}: TeamsSelectorProps): JSX.Element => {
  const [visible, setVisible] = useState(false);
  const { account } = useContext(SessionContext);

  const handleTeamChange = (teamsSelected: string[]) => {
    onTeamChange(teamsSelected);
  };

  return (
    <CustomDropdown
      maxWidth={maxWidth}
      overlayStyle={{ maxWidth: 'calc(100% - 20px)', margin: '0 20px' }}
      overlay={
        <TeamsMenu
          teamsSelected={userTeams}
          accountTeams={accountTeams}
          onClose={() => setVisible(false)}
          onTeamSelect={handleTeamChange}
          withSubmit={withSubmit}
          account={account}
          emptyLabel={emptyLabel}
        />
      }
      trigger={['click']}
      visible={visible}
      onVisibleChange={setVisible}
    >
      <div>
        {withEditBtn && (
          <Tooltip placement="bottomLeft" title={'Change user team'}>
            <FeatherIcon
              icon={'edit'}
              size="15"
              color="#A793F6"
              style={{ margin: editBtnRight ? '0 0 0 10px' : '0 10px 0 0' }}
            />
          </Tooltip>
        )}
      </div>
    </CustomDropdown>
  );
};

export default TeamsSelector;
